import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageToggle from '../../components/common/LanguageToggle';
import {
  Loader2, AlertCircle, User, Mail, Lock, Briefcase,
  CheckCircle, Clock, CloudCog, Users, Sun, Moon,
} from 'lucide-react';
import OperisLogo from '../../components/common/OperisLogo';
import AnimatedClock from '../../components/common/AnimatedClock';
import WaveRiver from '../../components/landing/WaveRiver';

const floatingTimes = [
  { text: '06:15', x: 6, y: 14, delay: 0, dur: 7 },
  { text: '11:30', x: 84, y: 20, delay: 3, dur: 9 },
  { text: '19:05', x: 88, y: 72, delay: 1, dur: 8 },
  { text: '22:40', x: 8, y: 82, delay: 5, dur: 6 },
];

/* ── Rotating rings around brand-panel clock ── */
const brandRings = [
  { diameter: 270, color: '#d97706', colorLight: '#d97706', opacity: 0.22, opacityLight: 0.18, dashed: true,  speed: 30 },
  { diameter: 310, color: '#f59e0b', colorLight: '#f59e0b', opacity: 0.16, opacityLight: 0.12, dashed: false, speed: 45 },
  { diameter: 350, color: '#ea580c', colorLight: '#b45309', opacity: 0.12, opacityLight: 0.08, dashed: true,  speed: 55 },
];

// Validation helper functions (matching backend)
const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, message: 'Email is required.' };
  }
  
  if (email.length > 256) {
    return { isValid: false, message: 'Email cannot exceed 256 characters.' };
  }
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|net|org|edu|gov|pk)$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Invalid Email format' };
  }
  
  return { isValid: true, message: '' };
};

const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, message: 'Password is required.' };
  }
  
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long.' };
  }
  
  if (password.length > 100) {
    return { isValid: false, message: 'Password cannot exceed 100 characters.' };
  }
  
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[^\s]+$/;
  if (!passwordRegex.test(password)) {
    return { 
      isValid: false, 
      message: 'Password must contain uppercase, lowercase, number, special character, and no spaces' 
    };
  }
  
  return { isValid: true, message: '' };
};

/* ── Brand panel (shared between main view & success view) ── */
const BrandPanel = ({ darkMode, features }) => {
  const panelBg = darkMode
    ? 'border-amber-500/[0.12] bg-white/[0.03]'
    : 'border-gray-200/70 bg-white/80 shadow-xl';
  const featureCard = darkMode
    ? 'border-amber-500/[0.1] bg-white/[0.03]'
    : 'border-gray-200 bg-gray-50';
  const featureIcon = darkMode ? 'text-amber-400' : 'text-amber-600';
  const featureLabel = darkMode ? 'text-white/60' : 'text-gray-600';

  return (
    <section className={`hidden flex-col items-center justify-center rounded-3xl border p-10 backdrop-blur-sm lg:flex transition-colors duration-500 ${panelBg}`}>
      <OperisLogo size="lg" light={darkMode} showTagline />

      <div className="relative mt-10 flex items-center justify-center" style={{ minHeight: 260, minWidth: 260 }}>
        {brandRings.map((ring, i) => (
          <div
            key={i}
            className="absolute pointer-events-none rounded-full"
            style={{
              width: ring.diameter,
              height: ring.diameter,
              top: '50%',
              left: '50%',
              marginTop: -ring.diameter / 2,
              marginLeft: -ring.diameter / 2,
              border: `1px ${ring.dashed ? 'dashed' : 'solid'} ${darkMode ? ring.color : ring.colorLight}`,
              opacity: darkMode ? ring.opacity : ring.opacityLight,
              animation: `spin ${ring.speed}s linear infinite`,
            }}
          />
        ))}
        <AnimatedClock
          size={200}
          compact
          showDigital={false}
          glowColor={darkMode ? '#d97706' : '#d97706'}
          lightDial={!darkMode}
        />
      </div>

      <div className="mt-10 w-full max-w-xs space-y-3">
        {features.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.label} className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-500 ${featureCard}`}>
              <Icon className={`h-4 w-4 flex-shrink-0 ${featureIcon}`} />
              <span className={`text-sm ${featureLabel}`}>{f.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ── Shared background shell ── */
const PageShell = ({ darkMode, toggleTheme, brandFeatures, children }) => {
  const { t } = useTranslation();
  const bg = darkMode ? 'bg-[#0f0f14]' : 'bg-gray-50';
  const gradientOverlay = darkMode
    ? 'bg-gradient-to-br from-[#0f0f14] via-[#1a1a24] to-[#2a1f0a]/70'
    : 'bg-gradient-to-br from-gray-50 via-white to-amber-50/50';
  const dotColor = darkMode ? 'rgba(217,119,6,0.05)' : 'rgba(245,158,11,0.06)';
  const particleColor = darkMode ? 'text-amber-400' : 'text-amber-500';
  const footerColor = darkMode ? 'text-white/25' : 'text-gray-400';

  return (
    <div className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${bg}`}>
      <div className={`absolute inset-0 transition-colors duration-500 ${gradientOverlay}`} />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${dotColor} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: 800, height: 800,
          background: darkMode
            ? 'radial-gradient(circle, rgba(217,119,6,0.08) 0%, transparent 55%)'
            : 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 50%)',
        }}
      />
      <WaveRiver darkMode={darkMode} />
      {floatingTimes.map((p, i) => (
        <div
          key={i}
          className={`absolute pointer-events-none select-none font-mono text-xs ${particleColor}`}
          style={{ left: `${p.x}%`, top: `${p.y}%`, opacity: 0.06, animation: `float ${p.dur}s ease-in-out ${p.delay}s infinite` }}
        >
          {p.text}
        </div>
      ))}

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col p-5 lg:p-8">
        {/* Theme + language */}
        <div className="flex justify-end items-center gap-2 pb-2">
          <button
            onClick={toggleTheme}
            className={`rounded-lg p-2 transition-all duration-300 ${
              darkMode
                ? 'text-white/60 hover:text-white hover:bg-white/10'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-label={t('auth.login.toggleThemeAria')}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <LanguageToggle className="rounded-lg" />
        </div>

        <div className="grid flex-1 items-center gap-6 lg:grid-cols-2">
          <BrandPanel darkMode={darkMode} features={brandFeatures} />
          {children}
        </div>

        {/* Footer */}
        <div className="py-6 text-center">
          <p className={`text-sm ${footerColor}`}>
            &copy; {new Date().getFullYear()} {t('layout.footer.copyrightPrefix')} &middot; {t('layout.footer.tagline')}
          </p>
          <div className="mt-3 flex items-center justify-center gap-2.5">
            <img
              src="/gblogo.png"
              alt="Greybeard"
              className={`h-6 w-auto ${darkMode ? 'opacity-40' : 'opacity-30'}`}
            />
            <span className={`text-xs font-medium ${darkMode ? 'text-white/25' : 'text-gray-400'}`}>
              {t('auth.login.footerPoweredBy')}{' '}
              <span className={`font-semibold ${darkMode ? 'text-white/35' : 'text-gray-500'}`}>Greybeard</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   REGISTER PAGE
   ═══════════════════════════════════════════ */
const RegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register, authError } = useAuth();
  const { darkMode, toggleTheme } = useTheme();

  const brandPanelFeatures = useMemo(
    () => [
      { icon: Clock, label: t('auth.register.panelFastOnboarding') },
      { icon: CloudCog, label: t('auth.register.panelSapWorkflow') },
      { icon: Users, label: t('auth.register.panelBuiltForTeams') },
    ],
    [t]
  );

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '',
    password: '', confirmPassword: '',
    employeeId: '', department: '',
  });
  
  // Real-time validation errors only for Email and Password
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  
  const [loading, setLoading]   = useState(false);
  const [localError, setLocalError] = useState('');
  const [step, setStep]         = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Real-time validation only for email and password
    if (name === 'email') {
      const validation = validateEmail(value);
      setEmailError(validation.message);
    } else if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordError(validation.message);
      // Also validate confirm password if it exists
      if (formData.confirmPassword) {
        if (value !== formData.confirmPassword) {
          setConfirmPasswordError('Passwords do not match');
        } else {
          setConfirmPasswordError('');
        }
      }
    } else if (name === 'confirmPassword') {
      if (value !== formData.password) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    }
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName) {
      setLocalError(t('auth.register.errors.firstLastRequired')); return false;
    }
    if (formData.firstName.length > 50) {
      setLocalError(t('auth.register.errors.firstNameMax')); return false;
    }
    if (formData.lastName.length > 50) {
      setLocalError(t('auth.register.errors.lastNameMax')); return false;
    }
    if (!formData.email || !formData.email.includes('@')) {
      setLocalError(t('auth.register.errors.emailInvalid')); return false;
    }
    
    return true;
  };

  const validateStep2 = () => {
    if (!formData.password || formData.password.length < 8) {
      setLocalError(t('auth.register.errors.passwordMin')); return false;
    }
    if (formData.password.length > 32) {
      setLocalError(t('auth.register.errors.passwordMax')); return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setLocalError(t('auth.register.errors.passwordMismatch')); return false;
    }
    
    return true;
  };

  const handleNextStep = () => { 
    setLocalError(''); 
    if (step === 1 && validateStep1()) setStep(2); 
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    if (!termsAccepted) {
      setLocalError('You must accept the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    setLocalError('');
    try {
      await register({
        firstName: formData.firstName, 
        lastName: formData.lastName,
        email: formData.email, 
        password: formData.password,
        employeeId: formData.employeeId, 
        department: formData.department,
      });
      setRegisteredEmail(formData.email);
      setRegistrationSuccess(true);

      // Clear form data
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        employeeId: '',
        department: '',
      });

      // Reset validation states
      setEmailError('');
      setPasswordError('');
      setConfirmPasswordError('');

      // Reset to step 1
      setStep(1);

      // Auto-redirect to login after 3 seconds
     

    } catch (error) {
      setLocalError(error.response?.data?.message || t('auth.register.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (registrationSuccess) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [registrationSuccess, navigate]);

  /* ── Theme tokens ── */
  const formCardBg   = darkMode ? 'border-amber-500/[0.12] bg-white/[0.04] backdrop-blur-sm' : 'border-gray-200/70 bg-white shadow-xl';
  const headingColor = darkMode ? 'text-white' : 'text-gray-900';
  const subColor     = darkMode ? 'text-white/50' : 'text-gray-500';
  const labelColor   = darkMode ? 'text-white/60' : 'text-gray-700';
  const inputClass   = darkMode
    ? 'border-amber-500/20 bg-white/[0.06] text-white placeholder-white/30 focus:ring-amber-500'
    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-amber-500';
  const errorBg      = darkMode ? 'border-red-500/20 bg-red-500/10' : 'border-red-200 bg-red-50';
  const errorIcon    = darkMode ? 'text-red-400' : 'text-red-500';
  const errorText    = darkMode ? 'text-red-300' : 'text-red-700';
  const linkColor    = darkMode ? 'text-white/40' : 'text-gray-600';
  const linkAccent   = darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700';
  const iconMuted    = darkMode ? 'text-white/30' : 'text-gray-400';
  const helpText     = darkMode ? 'text-white/30' : 'text-gray-500';
  const stepActive   = darkMode ? 'bg-amber-600 text-white' : 'bg-amber-500 text-white';
  const stepInactive = darkMode ? 'bg-white/10 text-white/40' : 'bg-gray-200 text-gray-500';
  const stepBar      = darkMode ? 'bg-amber-600' : 'bg-amber-500';
  const stepBarOff   = darkMode ? 'bg-white/10' : 'bg-gray-200';

  /* ── Success view ── */
  if (registrationSuccess) {
    return (
      <PageShell darkMode={darkMode} toggleTheme={toggleTheme} brandFeatures={brandPanelFeatures}>
        <div className="mx-auto w-full max-w-md animate-slide-up">
          <div className="mb-8 text-center">
            <div className={`mb-4 inline-flex h-20 w-20 items-center justify-center rounded-2xl border shadow-2xl ${
              darkMode ? 'border-white/[0.08] bg-white/[0.04]' : 'border-gray-200 bg-white'
            }`}>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className={`mb-2 text-3xl font-bold ${headingColor}`}>{t('auth.register.successTitle')}</h1>
            <p className={subColor}>{t('auth.register.successBody')}</p>
          </div>

          <div className={`rounded-2xl border p-8 text-center shadow-2xl transition-colors duration-500 ${formCardBg}`}>
            <div className={`mb-6 rounded-lg border p-4 ${
              darkMode ? 'border-green-500/20 bg-green-500/10' : 'border-green-200 bg-green-50'
            }`}>
              <p className={`font-medium ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                {t('auth.register.successForEmail', { email: registeredEmail })}
              </p>
              <p className={`mt-2 text-sm ${darkMode ? 'text-green-400/70' : 'text-green-600'}`}>
                {t('auth.register.successSignInHint')}
              </p>
            </div>

            <p className={subColor}>{t('auth.register.redirectHint')}</p>
            <div className={`mt-4 h-2.5 w-full rounded-full ${darkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
              <div className={`h-2.5 rounded-full ${darkMode ? 'bg-amber-500' : 'bg-amber-500'} animate-pulse`} />
            </div>
            <div className="pt-6">
              <Link to="/login" className={`inline-flex items-center gap-2 font-semibold ${linkAccent}`}>
                {t('auth.register.goToLogin')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  /* ── Main registration form ── */
  return (
    <PageShell darkMode={darkMode} toggleTheme={toggleTheme} brandFeatures={brandPanelFeatures}>
      <div className="mx-auto w-full max-w-md animate-slide-up">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4 lg:hidden">
            <OperisLogo size="lg" light={darkMode} />
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${headingColor}`}>{t('auth.register.title')}</h1>
          <p className={subColor}>{t('auth.register.subtitle')}</p>
        </div>

        {/* Card */}
        <div className={`rounded-2xl border p-8 shadow-2xl transition-colors duration-500 ${formCardBg}`}>
          {/* Progress steps */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${step >= 1 ? stepActive : stepInactive}`}>
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <div className={`w-16 h-1 ${step > 1 ? stepBar : stepBarOff}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${step >= 2 ? stepActive : stepInactive}`}>
                2
              </div>
            </div>
          </div>

          {/* Error */}
          {(authError || localError) && (
            <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 border ${errorBg}`}>
              <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${errorIcon}`} />
              <p className={`text-sm ${errorText}`}>{authError || localError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={step === 2 ? handleRegister : (e) => { e.preventDefault(); handleNextStep(); }}>
            {/* ── Step 1: personal info ── */}
            {step === 1 && (
              <div className="space-y-5 animate-fade-in">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.register.firstName')}</label>
                    <div className="relative">
                      <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconMuted}`} />
                      <input 
                        id="firstName" 
                        name="firstName" 
                        type="text" 
                        value={formData.firstName} 
                        onChange={handleChange}
                        placeholder="John" 
                        disabled={loading} 
                        maxLength={50}
                        className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`} 
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lastName" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.register.lastName')}</label>
                    <input id="lastName" name="lastName" type="text" value={formData.lastName} onChange={handleChange}
                      placeholder="Doe" disabled={loading} maxLength={50}
                      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`} />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.register.email')}</label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconMuted}`} />
                    <input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleChange}
                      placeholder="you@company.com" 
                      disabled={loading}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${
                        emailError ? 'border-red-500 focus:ring-red-500' : inputClass
                      }`} 
                    />
                  </div>
                  {emailError && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="employeeId" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.register.employeeIdOptional')}</label>
                  <div className="relative">
                    <Briefcase className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconMuted}`} />
                    <input 
                      id="employeeId" 
                      name="employeeId" 
                      type="text" 
                      value={formData.employeeId} 
                      onChange={handleChange}
                      placeholder="EMP-001" 
                      disabled={loading}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`} 
                    />
                  </div>
                  <p className={`mt-1 text-xs ${helpText}`}>{t('auth.register.employeeIdHelp')}</p>
                </div>

                <div>
                  <label htmlFor="department" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.register.department')}</label>
                  <select id="department" name="department" value={formData.department} onChange={handleChange} disabled={loading}
                    className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`}>
                    <option value="" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>{t('auth.register.selectDepartment')}</option>
                    <option value="design_pm" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Design &amp; PM</option>
                    <option value="engineering" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Engineering</option>
                    <option value="fabrication" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Fabrication</option>
                    <option value="fitting_paint_test" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Fitting, Paint &amp; Test</option>
                    <option value="hr" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Human Resources</option>
                    <option value="impeller" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Impeller</option>
                    <option value="JBT" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>JBT</option>
                    <option value="production" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Production</option>
                    <option value="service" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>Service</option>
                    <option value="TSI" style={{ backgroundColor: darkMode ? '#0f0f14' : 'white' }}>TSI</option>
                  </select>
                </div>

                <button 
                  type="submit" 
                  disabled={loading || !!emailError}
                  className={`w-full flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'border border-white/15 bg-white/[0.06] text-white hover:bg-white/[0.1]'
                      : 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg'
                  }`}>
                  {t('auth.register.continue')}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            )}

            {/* ── Step 2: security ── */}
            {step === 2 && (
              <div className="space-y-5 animate-fade-in">
                <div>
                  <label htmlFor="password" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.login.password')}</label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconMuted}`} />
                    <input 
                      id="password" 
                      name="password" 
                      type="password" 
                      value={formData.password} 
                      onChange={handleChange}
                      placeholder="••••••••" 
                      disabled={loading} 
                      maxLength={100}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${
                        passwordError ? 'border-red-500 focus:ring-red-500' : inputClass
                      }`} 
                    />
                  </div>
                  <p className={`mt-1 text-xs ${helpText}`}>{t('auth.register.passwordHint')}</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.register.confirmPassword')}</label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconMuted}`} />
                    <input 
                      id="confirmPassword" 
                      name="confirmPassword" 
                      type="password" 
                      value={formData.confirmPassword} 
                      onChange={handleChange}
                      placeholder="••••••••" 
                      disabled={loading} 
                      maxLength={100}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${
                        confirmPasswordError ? 'border-red-500 focus:ring-red-500' : inputClass
                      }`} 
                    />
                  </div>
                  {confirmPasswordError && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {confirmPasswordError}
                    </p>
                  )}
                </div>

                <div className="flex items-start"> 
                  <input 
                    id="terms" 
                    type="checkbox" 
                    checked={termsAccepted} 
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className={`w-4 h-4 mt-1 rounded focus:ring-amber-500 ${
                      darkMode 
                        ? 'text-amber-500 border-white/20 bg-white/[0.06]' 
                        : 'text-amber-500 border-gray-300 bg-white'
                    }`} 
                  />
                  <label htmlFor="terms" className={`ml-2 text-sm ${darkMode ? 'text-white/50' : 'text-gray-600'}`}>
                    {t('auth.register.termsPrefix')}{' '}
                    <Link to="/terms" target="_blank" className={linkAccent}>{t('auth.register.termsLink')}</Link>
                    {' '}{t('auth.register.and')}{' '}
                    <Link to="/privacy-policy" target="_blank" className={linkAccent}>{t('auth.register.privacyLink')}</Link>
                  </label>
                </div>

                <div className="flex gap-3">
                  <button 
                    type="button" 
                    onClick={() => setStep(1)} 
                    disabled={loading}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-semibold transition-all duration-300 disabled:opacity-50 ${
                      darkMode
                        ? 'border border-white/15 bg-white/[0.04] text-white/70 hover:bg-white/[0.08]'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                    </svg>
                    {t('auth.register.back')}
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading || !termsAccepted || !!passwordError || !!confirmPasswordError}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode
                        ? 'bg-gradient-to-r from-amber-600 to-amber-500 shadow-amber-500/20'
                        : 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-amber-500/20'
                    }`}>
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>{t('auth.register.createAccountCta')} <CheckCircle className="w-5 h-5" /></>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Login link */}
          <p className={`mt-6 text-center text-sm ${linkColor}`}>
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className={`font-semibold ${linkAccent}`}>{t('auth.register.signIn')}</Link>
          </p>
        </div>
      </div>
    </PageShell>
  );
};

export default RegisterPage;